<template>
  <el-dialog
    :visible.sync="dialogVisible"
    :before-close="handleClose"
    :close-on-press-escape="true"
    custom-class="elsa-modal"
    :center="false"
    width="255"
    top="100px"
    v-on:closed="closed"
  >
    <div slot="title">
      <h1>{{ $t("recover_password.title") }}</h1>
      <p>{{ $t("recover_password.enter_email_to_reset") }}</p>
    </div>

    <slot v-if="askingResetPassword">
      <api-errors :errors="errors" />
      <el-form :model="form" :rules="rules" ref="form">
        <elsa-input
          name="email"
          :label="$t('resend_confirmation.email_field.label')"
          :placeholder="$t('resend_confirmation.email_field.placeholder')"
          v-model.trim.lazy="form.email"
          ref="emailField"
        />
        <span class="dialog-footer elsa-button-footer">
          <primary-button
            ref="submit"
            v-loading="loading"
            type="submit"
            @click.native="submitRecoverPassword"
            disabled
          >
            {{ $t("recover_password.reset_password") }}
          </primary-button>
        </span>
      </el-form>
    </slot>
    <slot v-else>
      <img src="@/assets/images/recover-password.svg" alt="" />
      <h2 class="info">{{ $t("recover_password.sent_email_check_inbox") }}</h2>
      <span slot="footer" class="dialog-footer">
        <primary-button @click.native="handleClose">{{
          $t("recover_password.got_it")
        }}</primary-button>
      </span>
    </slot>
  </el-dialog>
</template>

<script>
import PrimaryButton from "@/components/common/buttons/PrimaryButton";
import ElsaInput from "@/components/common/Input";
import ApiErrors from "@/components/common/ApiErrors";
import {
  handleFormValidation,
  handleSubmitButtonState,
  isPropEmpty,
} from "@/helpers/formValidationHelper";
import axios from "@/utils/axios";
import { compose, isEmpty, either, isNil, not } from "ramda";

export default {
  name: "RecoverPassword",
  props: ["dialogVisible"],
  components: {
    PrimaryButton,
    ElsaInput,
    ApiErrors,
  },
  data() {
    return {
      loading: false,
      form: {},
      askingResetPassword: true,
      sendPasswordSuccess: false,
      errors: [],
      rules: {
        email: [
          {
            message: this.$t('recover_password.please_input_email'),
            trigger: ["blur"],
            validator: (rule, value, callback) => {
              const isValid = compose(not, either(isNil, isEmpty));
              if (!isValid(value)) {
                return callback(new Error(this.$t('recover_password.please_input_email')));
              }
              return callback();
            },
          },
          {
            type: "email",
            message: this.$t('recover_password.input_correct_email'),
            trigger: "blur",
          },
        ],
      },
    };
  },
  methods: {
    showModal() {
      this.askingResetPassword = true;
      this.sendPasswordSuccess = false;
      this.dialogVisible = true;
    },
    handleClose() {
      this.dialogVisible = false;
      this.resetForm();
    },
    onFormChange() {
      handleSubmitButtonState(
        this.$refs.submit.$el,
        !isPropEmpty("email")(this.form)
      );
    },
    submitRecoverPassword(e) {
      e.preventDefault();
      this.loading = true;
      handleFormValidation(this.$refs.form, this.recoverPasswordRequest, () => {
        this.dialogVisible = true;
      }).finally(() => (this.loading = false));
    },
    recoverPasswordRequest() {
      axios
        .post("/v1/accounts/reset_password/", { ...this.form })
        .then(() => {
          this.resetForm();
          this.resetEmailSentSuccess();
          this.loading = false;
        })
        .catch((error) => {
          this.loading = false;
          if (error.response.status === 404) {
            this.resetForm();
            this.resetEmailSentSuccess();
          } else {
            this.errors = error.response.data || [
              this.$t("common.generic_error_message"),
            ];
          }
        });
    },
    resetForm() {
      this.$refs.form.resetFields();
      this.errors = [];
    },
    resetEmailSentSuccess() {
      this.askingResetPassword = false;
      this.sendPasswordSuccess = true;
    },
    closed() {
      this.$emit("closed");
      this.askingResetPassword = true;
    },
  },
  watch: {
    dialogVisible: {
      immediate: true,
      deep: true,
      handler(visible) {
        if (visible) {
          this.resetForm();
          this.onFormChange();
        }
      },
    },
    form: {
      deep: true,
      handler() {
        return this.onFormChange()
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.elsa-button {
  width: 202px;
}

h2.info {
  line-height: 28px;
  padding-bottom: 32px;
  width: 100%;
}

img {
  margin-top: 8px;
}
</style>
